/**
 * Money.
 *
 * Money is an integer number of paisa. Never a float, never a string, never a
 * Number with a decimal point. 100 paisa = 1 rupee. Rounding happens once, at
 * a named boundary, with a stated rule — not accidentally in a division.
 *
 * Formatting happens only at the display boundary, via `formatMoney`.
 */

declare const paisaBrand: unique symbol;

export type Money = number & { readonly [paisaBrand]: 'Paisa' };

export const ZERO: Money = 0 as Money;

/** Wrap an integer count of paisa, as it arrives from the API. */
export function paisa(value: number): Money {
  if (!Number.isInteger(value)) {
    throw new Error(`Money must be an integer number of paisa, received ${value}`);
  }
  return value as Money;
}

/** Build money from whole rupees — for rate entry, where staff type rupees. */
export function rupees(value: number): Money {
  return paisa(Math.round(value * 100));
}

export function addMoney(...amounts: readonly Money[]): Money {
  return paisa(amounts.reduce<number>((total, amount) => total + amount, 0));
}

export function subtractMoney(a: Money, b: Money): Money {
  return paisa(a - b);
}

/** Multiply by a count (days, kilometres). Rounds half up, once. */
export function multiplyMoney(amount: Money, factor: number): Money {
  return paisa(Math.round(amount * factor));
}

/** Apply a percentage — discounts, commission, VAT. Rounds half up, once. */
export function percentOfMoney(amount: Money, percent: number): Money {
  return paisa(Math.round((amount * percent) / 100));
}

export type NumberGrouping = 'south_asian' | 'western';

export interface MoneyFormat {
  /** "Rs" for Nepal. Written before the digits, with a space. */
  readonly prefix: string;
  readonly grouping: NumberGrouping;
}

/**
 * Group digits. South Asian grouping puts the last three together and then
 * pairs upward: 12,50,000. Nepali staff read totals wrong when they are grouped
 * by threes, so this is a per-client config value, not a default.
 */
function groupDigits(digits: string, grouping: NumberGrouping): string {
  if (grouping === 'western') {
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  if (digits.length <= 3) return digits;
  const last3 = digits.slice(-3);
  const rest = digits.slice(0, -3);
  return `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}`;
}

/**
 * Render money for a human. Paisa are shown only when they are not zero —
 * a cash business quotes round rupees and a trailing ".00" is noise in a column.
 */
export function formatMoney(amount: Money, format: MoneyFormat): string {
  const negative = amount < 0;
  const absolute = Math.abs(amount);
  const wholeRupees = Math.trunc(absolute / 100);
  const remainder = absolute % 100;
  const body = groupDigits(String(wholeRupees), format.grouping);
  const withPaisa = remainder === 0 ? body : `${body}.${String(remainder).padStart(2, '0')}`;
  return `${negative ? '−' : ''}${format.prefix} ${withPaisa}`;
}

/** Digits only, for a table cell that carries its unit in the column header. */
export function formatMoneyDigits(amount: Money, grouping: NumberGrouping): string {
  const wholeRupees = Math.trunc(Math.abs(amount) / 100);
  const remainder = Math.abs(amount) % 100;
  const body = groupDigits(String(wholeRupees), grouping);
  const withPaisa = remainder === 0 ? body : `${body}.${String(remainder).padStart(2, '0')}`;
  return `${amount < 0 ? '−' : ''}${withPaisa}`;
}

/** Parse what a staff member typed into a rupee field. Returns null if unusable. */
export function parseRupeeInput(input: string): Money | null {
  const cleaned = input.replace(/[\s,]/g, '');
  if (cleaned === '') return null;
  if (!/^-?\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  return paisa(Math.round(Number(cleaned) * 100));
}
